import "./Rankings.css"
import { getAllIdolNamesWithGroup, getStartToEndImages } from "../../requests/images.js"
import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";

import ImageWithPlaceHolder from "../../components/ImageWithPlaceHolder/ImageWithPlaceholder.jsx";

import crown from "../../assets/crown.png"

const Rankings = () => {
    const [images, setImages] = useState([]);
    const [lastDocId, setLastDocId] = useState(null);

    const [isBottom, setIsBottom] = useState(false);
    const [searchMore, setSearchMore] = useState(true);
    const [isLoadingMain, setIsLoadingMain] = useState(true);
    const [imagesLoaded, setImagesLoaded] = useState(0);
    const [isLoading] = useState(false);
    const [showEndIndicator, setShowEndIndicator] = useState(false);
    const [showScrollTopButton, setShowScrollTopButton] = useState(false);

    const [idolGroups, setIdolGroups] = useState([]);
    const [daysUntilNextMonth, setDaysUntilNextMonth] = useState("X");
    
    const navigate = useNavigate();
    const [queryParameters] = useSearchParams();
    const selectedFilter = queryParameters.get("filter");

    const fetchImages = useCallback(async (idolName, cursor, canSearchMore) => {
        if (canSearchMore) {
            // console.log("fetching");
            setIsLoadingMain(true);
            const pagination = await getStartToEndImages(idolName, 20, cursor);

            if (pagination.images.length > 0) {
                setImages((prev) => [...prev, ...pagination.images])
                setLastDocId(pagination.lastDocId);
            } else { // if we have no more images to display, then we shouldn't be able to fetch anymore images.
                // console.log("done")
                setShowEndIndicator(true);
                setSearchMore(false);
                setIsLoadingMain(false);
            }
        }
    }, [])

    const handleScroll = useCallback(() => {
        const scrollTop = window.scrollY;
        const windowHeight = window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;

        setShowScrollTopButton(scrollTop > 2000);

        // indicate that we are at the bottom of the screen
        if (scrollTop + windowHeight >= documentHeight - 5) {
            setIsBottom(true);
        }
    }, []);

    // on initial render
    useEffect(() => {
        const fetchAllIdolGroups = async () => {
            const uniqueIdolGroups = await getAllIdolNamesWithGroup();
            setIdolGroups(uniqueIdolGroups);
        };
        
        window.addEventListener("scroll", handleScroll)
        fetchAllIdolGroups();
        getDaysUntilNextMonth();

        return () => window.removeEventListener("scroll", handleScroll);

    }, [handleScroll])

    // trigger fetch when user selects a new idol
    useEffect(() => {
        // console.log(`filter: ${queryParameters.get("filter")}`);
        setSearchMore(true);
        setLastDocId(null);
        setImages([]);
        setImagesLoaded(0);
        setShowEndIndicator(false);
        fetchImages(selectedFilter, null, true);
    }, [selectedFilter, fetchImages])

    // trigger fetch when user reaches the bottom of the page
    useEffect(() => {
        if (isBottom) {
            // same as await fetchImages(...); setIsBottom(false)
            fetchImages(selectedFilter, lastDocId, searchMore).then(() => {
                setIsBottom(false);
            })
        }
    }, [isBottom, fetchImages, selectedFilter, lastDocId, searchMore])

    useEffect(() => {
        // console.log(`imagesLoaded: ${imagesLoaded}, numImages: ${images.length}`)
        if (images.length !== 0 && imagesLoaded >= images.length) {
          console.log('All images loaded');
          setImagesLoaded(images.length);
          setIsLoadingMain(false);
        } 
      }, [imagesLoaded, images.length]);

    const handleImageLoad = () => {
        setImagesLoaded(prev => prev + 1);
    };

    const topFunction = () => {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'smooth'
        });
    }

    const handleSelect = (e) => {
        navigate(`/rankings?filter=${e.target.value}`)
        setSearchMore(true);
        setLastDocId(null);
        setImages([]);
        setImagesLoaded(0);
        setShowEndIndicator(false);
    }

    const getDaysUntilNextMonth = () => {
        const today = new Date();
        const currentMonth = today.getMonth();
        const currentYear = today.getFullYear();

        const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
        const nextMonthYear = currentMonth === 11 ? currentYear + 1 : currentYear;

        const firstDayOfNextMonth = new Date(nextMonthYear, nextMonth, 1);

        const diffInMs = firstDayOfNextMonth - today;
        const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));

        setDaysUntilNextMonth(diffInDays);
        // return diffInDays
    }

    const getDaysCountClass = () => {
        if (daysUntilNextMonth > 19) {
            return "text-[#9ff7a5]"
        } else if (daysUntilNextMonth > 9) {
            return "dark:text-[#ded380] text-[#fcba03]"
        } else if (daysUntilNextMonth > 4) {
            return "text-[#c280a8]"
        } else {
            return "text-[#ff5770]"
        }
    }

    const getRankOneStyle = (index) => {
        if (index === 0) {
            return "idolCardRankOne shadow-[#fcba03] bg-gradient-to-r from-[#fcba03] to-[#de7134]"
        } else {
            return "idolCard"
        }
    }

    return (
        <div className="Rankings relative flex-1">
            {/* headers */}
            <div className="flex flex-col items-center md:justify-between md:flex-row md:gap-6 gap-1 px-6 mb-7 md:mb-11 z-10 relative w-full ">
                <div className={`md:text-3xl text-2xl dark:text-white text-center`}>
                    NEW IMAGE CYCLE IN <span className={getDaysCountClass()}>{daysUntilNextMonth}</span> DAY<span className={daysUntilNextMonth === 1 ? "hidden" : "inline"}>S</span>
                </div>
                <div className="flex flex-row justify-center items-center md:text-3xl text-2xl dark:text-white">
                    <label>Filter: </label>

                    <select 
                        name="idols" 
                        className="min-w-40 bg-white dark:bg-black border-black dark:border-white border-2 rounded-md ml-2 p-[0.1rem]" 
                        value={queryParameters.get("filter") || "All"} 
                        onChange={handleSelect}
                    >
                        <option>All</option>
                        {idolGroups.sort((a, b) => {
                            if (a.groupName > b.groupName) {
                                return 1;
                            } else if (a.groupName < b.groupName) {
                                return -1;
                            } else {
                                return 0;
                            }
                        }).map((idolGroups, index) => {
                            return <option 
                                value={idolGroups.idolName} key={idolGroups.idolName}>
                                    {`${idolGroups.idolName.replace(/[0-9]/g, '')} (${idolGroups.groupName})`}
                                </option>
                        })}
                    </select>
                </div>
            </div>

            {/* message if user doesn't see any images */}
            {(images.length === 0 && !isLoadingMain) && <div className="noImagesMessage text-center text-2xl md:text-4xl mt-40">
                Don't see any images? try reloading!
            </div>}

            {/* gallery */}
            <div className="flex flex-row flex-wrap gap-x-3 gap-y-7 md:gap-x-6 md:gap-y-10 md:p-8 p-4 justify-center">
                {images.map((image, index) => {
                    return (
                        <div key={image.id} className={`relative rounded-xl p-1 dark:bg-black bg-white dark:text-white shadow-2xl ${getRankOneStyle(index)}`}>
                            <ImageWithPlaceHolder 
                                src={image.firebaseUrl || image.url} 
                                link={image.originUrl} 
                                alt={image.imageName} 
                                handleImageLoad={handleImageLoad} 
                                width={image.width} 
                                height={image.height} 
                                idolName={image.idolName} 
                                withAnchor={true}
                                className="box-border md:border-4 border-2 border-black dark:border-gray-500 md:h-[20rem] h-[10rem] rounded-xl"
                            />
                            {/* <div>{image.idolName}</div> */}
                            {/* stats */}
                            <div className="flex flex-row items-center md:gap-4 flex-wrap">
                                <div className="md:text-[2.5rem] text-[1rem]">{index + 1}.</div>
                                <div className="flex flex-col justify-center flex-1 items-center">
                                    <div className="text-center text-[1rem] md:text-[1.5rem]">W: {image.numWins} L: {image.numLosses}</div>
                                    <div className="text-center text-[0.7rem] md:text-[1rem]">
                                        Score: {image.score}
                                    </div>
                                </div>
                            </div>
                            {index === 0 && <img className="absolute lg:w-[8rem] md:w-[8rem] w-[4rem] h-auto lg:top-[-6rem] md:top-[-6rem] top-[-3rem] left-[50%] translate-x-[-50%]" src={crown} alt="crown" />}
                        </div>
                    )
                }
                )}
            </div>
            {isLoading &&
                <div className="loadingScreen">
                    <div className="loader"></div>
                    <div className="darkOverlay"></div>
                </div>
            }

            {isLoadingMain &&
                <div className="loadingMain fixed bottom-4 left-4 rounded-[50%] w-14 h-14 border-[#067c91] dark:border-[#72d3e4] border-8 border-l-transparent border-r-transparent dark:border-l-transparent dark:border-r-transparent"></div>
            }

            {showEndIndicator &&
                <div className="eolMessage flex justify-center md:text-[2rem] text-[1.5rem] dark:text-white pb-8">END OF LIST</div>
            }

            <button id="myBtn" onClick={() => topFunction()} className={`fixed md:bottom-[20px] bottom-[10px] right-[10px] md:right-[30px] text-white m-4 text-[2rem] z-99 rounded-full px-4 bg-gray-700 shadow-2xl ${showScrollTopButton ? "block" : "hidden"}`}>↑</button>
        </div>
    )
}

export default Rankings;
